/**
 * PersonaRoundTablePage — all 6 investment personas critiquing the portfolio
 * side-by-side in a single view.
 *
 * Uses the existing /api/portfolio/what-would endpoint (returns all 6 in
 * parallel). Each persona may succeed or fail independently; we render an
 * error card for any that failed and still compute consensus from those that
 * succeeded.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Users, Scale, AlertTriangle, ArrowUp, ArrowDown, RefreshCw, Loader2,
  CheckCircle2, XCircle, TrendingUp, TrendingDown,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '@/lib/api'

// ── Types ────────────────────────────────────────────────────────────────────

interface AddPosition {
  ticker: string
  allocation_pct: number
  reason: string
}

interface ReducePosition {
  ticker: string
  from_pct: number
  to_pct: number
  reason: string
}

interface PersonaCritique {
  persona_id: string
  persona_name: string
  persona_style?: string
  persona_color?: string
  persona_initials?: string
  verdict?: string
  biggest_problem?: string
  what_id_change?: string[]
  what_id_keep?: string[]
  add_positions?: AddPosition[]
  reduce_positions?: ReducePosition[]
  risk_score?: number
  risk_label?: string
  error?: string
}

interface WhatWouldAllResponse {
  personas: PersonaCritique[]
}

// ── Colour maps (match PersonasPage for consistency) ────────────────────────

const PERSONA_BG: Record<string, string> = {
  green:  'bg-emerald-500',
  blue:   'bg-blue-500',
  gold:   'bg-amber-500',
  purple: 'bg-purple-500',
  pink:   'bg-pink-500',
  red:    'bg-rose-500',
}

const PERSONA_BORDER: Record<string, string> = {
  green:  'border-emerald/30',
  blue:   'border-blue/30',
  gold:   'border-amber/30',
  purple: 'border-purple/30',
  pink:   'border-pink/30',
  red:    'border-rose/30',
}

const PERSONA_TEXT: Record<string, string> = {
  green:  'text-emerald',
  blue:   'text-blue-light',
  gold:   'text-amber',
  purple: 'text-purple-light',
  pink:   'text-pink-400',
  red:    'text-rose-light',
}

// ── Small helpers ───────────────────────────────────────────────────────────

function riskColor(score: number): string {
  if (score <= 3) return 'bg-emerald'
  if (score <= 5) return 'bg-blue-500'
  if (score <= 7) return 'bg-amber'
  return 'bg-rose-500'
}

function PersonaAvatar({ initials, color }: { initials: string; color: string }) {
  return (
    <div className={clsx(
      'rounded-full flex items-center justify-center font-bold text-white shrink-0 w-10 h-10 text-sm',
      PERSONA_BG[color] ?? 'bg-surface-hover',
    )}>
      {initials}
    </div>
  )
}

// ── Risk meter ───────────────────────────────────────────────────────────────

function RiskMeter({ score, label }: { score: number; label: string }) {
  const pct = Math.max(0, Math.min(100, (score / 10) * 100))
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="panel-title">Risk Score</span>
        <span className="text-xs font-bold text-text-primary">
          {score}/10 <span className="text-text-muted font-normal">· {label}</span>
        </span>
      </div>
      <div className="h-2 bg-surface-hover rounded-full overflow-hidden">
        <div
          className={clsx('h-full rounded-full transition-all', riskColor(score))}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

// ── Error card (persona failed) ──────────────────────────────────────────────

function PersonaErrorCard({ persona }: { persona: PersonaCritique }) {
  const color = persona.persona_color ?? ''
  return (
    <div className={clsx('card p-5 flex flex-col gap-3 border border-dashed', PERSONA_BORDER[color] ?? 'border-rose/30')}>
      <div className="flex items-center gap-3">
        <PersonaAvatar initials={persona.persona_initials ?? '??'} color={color} />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-text-primary">{persona.persona_name}</h3>
          <p className={clsx('text-xs font-medium', PERSONA_TEXT[color] ?? 'text-text-muted')}>
            {persona.persona_style ?? ''}
          </p>
        </div>
        <XCircle size={16} className="text-rose-light shrink-0" aria-hidden />
      </div>
      <div className="flex items-start gap-2 text-xs text-rose-light bg-rose/5 border border-rose/20 rounded p-2">
        <AlertTriangle size={14} className="shrink-0 mt-0.5" aria-hidden />
        <span className="break-words">Analysis unavailable: {persona.error}</span>
      </div>
    </div>
  )
}

// ── Persona card (successful critique) ──────────────────────────────────────

function PersonaCritiqueCard({ persona }: { persona: PersonaCritique }) {
  const color = persona.persona_color ?? ''
  return (
    <div className={clsx('card card-hover p-5 flex flex-col gap-4 border', PERSONA_BORDER[color] ?? 'border-border')}>
      {/* Header */}
      <div className="flex items-center gap-3">
        <PersonaAvatar initials={persona.persona_initials ?? '??'} color={color} />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-text-primary">{persona.persona_name}</h3>
          <p className={clsx('text-xs font-medium truncate', PERSONA_TEXT[color] ?? 'text-text-muted')}>
            {persona.persona_style}
          </p>
        </div>
      </div>

      {/* Verdict */}
      {persona.verdict && (
        <div>
          <div className="flex items-center gap-1.5 mb-1">
            <Scale size={12} className="text-blue-light" aria-hidden />
            <span className="panel-title">Verdict</span>
          </div>
          <p className="text-sm text-text-primary leading-snug font-medium">{persona.verdict}</p>
        </div>
      )}

      {/* Biggest problem */}
      {persona.biggest_problem && (
        <div className="border border-amber/20 bg-amber/5 rounded p-2.5">
          <div className="flex items-center gap-1.5 mb-1">
            <AlertTriangle size={12} className="text-amber" aria-hidden />
            <span className="panel-title text-amber">Biggest Problem</span>
          </div>
          <p className="text-xs text-text-secondary leading-snug">{persona.biggest_problem}</p>
        </div>
      )}

      {/* Risk score meter */}
      {typeof persona.risk_score === 'number' && (
        <RiskMeter score={persona.risk_score} label={persona.risk_label ?? ''} />
      )}

      {/* What they'd change */}
      {persona.what_id_change && persona.what_id_change.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-1.5">
            <TrendingDown size={12} className="text-rose-light" aria-hidden />
            <span className="panel-title">
              What They'd Change
            </span>
          </div>
          <ul className="space-y-1">
            {persona.what_id_change.map((item, i) => (
              <li key={i} className="text-xs text-text-secondary leading-snug flex gap-1.5">
                <span className="text-rose-light shrink-0">—</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* What they'd keep */}
      {persona.what_id_keep && persona.what_id_keep.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-1.5">
            <CheckCircle2 size={12} className="text-emerald" aria-hidden />
            <span className="panel-title">
              What They'd Keep
            </span>
          </div>
          <ul className="space-y-1">
            {persona.what_id_keep.map((item, i) => (
              <li key={i} className="text-xs text-text-secondary leading-snug flex gap-1.5">
                <span className="text-emerald shrink-0">+</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Add / Reduce tickers */}
      {(persona.add_positions?.length || persona.reduce_positions?.length) ? (
        <div className="grid grid-cols-2 gap-2 pt-1">
          <div>
            <div className="flex items-center gap-1 mb-1">
              <ArrowUp size={11} className="text-emerald" aria-hidden />
              <span className="text-[9px] uppercase tracking-wider text-text-muted font-semibold">Add</span>
            </div>
            {persona.add_positions?.length ? (
              <div className="flex flex-wrap gap-1">
                {persona.add_positions.map((p, i) => (
                  <span
                    key={i}
                    className="text-[10px] px-1.5 py-0.5 rounded bg-emerald/10 text-emerald border border-emerald/20"
                    title={p.reason}
                  >
                    {p.ticker} {(p.allocation_pct ?? 0).toFixed(0)}%
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-[10px] text-text-muted italic">—</p>
            )}
          </div>
          <div>
            <div className="flex items-center gap-1 mb-1">
              <ArrowDown size={11} className="text-rose-light" aria-hidden />
              <span className="text-[9px] uppercase tracking-wider text-text-muted font-semibold">Reduce</span>
            </div>
            {persona.reduce_positions?.length ? (
              <div className="flex flex-wrap gap-1">
                {persona.reduce_positions.map((p, i) => (
                  <span
                    key={i}
                    className="text-[10px] px-1.5 py-0.5 rounded bg-rose/10 text-rose-light border border-rose/20"
                    title={p.reason}
                  >
                    {p.ticker} {(p.from_pct ?? 0).toFixed(0)}→{(p.to_pct ?? 0).toFixed(0)}%
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-[10px] text-text-muted italic">—</p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}

// ── Skeleton card (loading) ──────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="card p-5 flex flex-col gap-4 border border-border">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full skeleton" />
        <div className="flex-1 space-y-2">
          <div className="h-3 skeleton w-24" />
          <div className="h-2 skeleton w-32" />
        </div>
      </div>
      <div className="h-3 skeleton w-full" />
      <div className="h-3 skeleton w-3/4" />
      <div className="h-12 skeleton" />
      <div className="h-2 skeleton w-full" />
      <div className="space-y-1">
        <div className="h-2 skeleton w-5/6" />
        <div className="h-2 skeleton w-4/6" />
        <div className="h-2 skeleton w-3/6" />
      </div>
    </div>
  )
}

// ── Consensus logic ─────────────────────────────────────────────────────────

interface ConsensusItem {
  ticker: string
  count: number
  reasons: string[]
}

interface ConsensusData {
  avgRisk: number | null
  successfulCount: number
  failedCount: number
  consensusSells: ConsensusItem[]
  consensusBuys: ConsensusItem[]
  problemThemes: { phrase: string; count: number }[]
}

// Simple stopword list to dedupe "biggest problem" phrases into rough themes.
const STOPWORDS = new Set([
  'the','a','an','and','or','but','of','to','in','on','at','for','with','by',
  'is','are','was','were','be','been','being','it','its','this','that','these',
  'those','your','you','portfolio','has','have','had','too','much','very','so',
  'as','from','into','than','then','there','their','they','them','which','what',
  'not','no','any','all','one','more','most','some','per','pct','percent','cent',
  'over','under','about','could','would','should','will','can','may',
])

function extractThemes(problems: string[]): { phrase: string; count: number }[] {
  // Cluster on significant single words and bigrams, return the top themes.
  const counts: Record<string, number> = {}
  for (const raw of problems) {
    if (!raw) continue
    const tokens = raw
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .split(/\s+/)
      .filter(t => t.length > 3 && !STOPWORDS.has(t))
    // unigrams
    for (const t of tokens) {
      counts[t] = (counts[t] ?? 0) + 1
    }
    // bigrams
    for (let i = 0; i < tokens.length - 1; i++) {
      const bg = `${tokens[i]} ${tokens[i + 1]}`
      counts[bg] = (counts[bg] ?? 0) + 1
    }
  }
  return Object.entries(counts)
    .filter(([, c]) => c >= 2)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([phrase, count]) => ({ phrase, count }))
}

function computeConsensus(personas: PersonaCritique[]): ConsensusData {
  const successful = personas.filter(p => !p.error)
  const failed = personas.filter(p => p.error)

  // Average risk
  const riskScores = successful
    .map(p => p.risk_score)
    .filter((n): n is number => typeof n === 'number')
  const avgRisk = riskScores.length
    ? riskScores.reduce((s, n) => s + n, 0) / riskScores.length
    : null

  // Reduce counts
  const reduceMap: Record<string, ConsensusItem> = {}
  for (const p of successful) {
    for (const rp of p.reduce_positions ?? []) {
      if (!rp?.ticker) continue
      const key = rp.ticker.toUpperCase().trim()
      if (!reduceMap[key]) reduceMap[key] = { ticker: key, count: 0, reasons: [] }
      reduceMap[key].count += 1
      if (rp.reason) reduceMap[key].reasons.push(`${p.persona_name}: ${rp.reason}`)
    }
  }
  const consensusSells = Object.values(reduceMap)
    .filter(i => i.count >= 3)
    .sort((a, b) => b.count - a.count)

  // Add counts
  const addMap: Record<string, ConsensusItem> = {}
  for (const p of successful) {
    for (const ap of p.add_positions ?? []) {
      if (!ap?.ticker) continue
      const key = ap.ticker.toUpperCase().trim()
      if (!addMap[key]) addMap[key] = { ticker: key, count: 0, reasons: [] }
      addMap[key].count += 1
      if (ap.reason) addMap[key].reasons.push(`${p.persona_name}: ${ap.reason}`)
    }
  }
  const consensusBuys = Object.values(addMap)
    .filter(i => i.count >= 2)
    .sort((a, b) => b.count - a.count)

  const problemThemes = extractThemes(successful.map(p => p.biggest_problem ?? ''))

  return {
    avgRisk,
    successfulCount: successful.length,
    failedCount: failed.length,
    consensusSells,
    consensusBuys,
    problemThemes,
  }
}

// ── Consensus panel ─────────────────────────────────────────────────────────

function ConsensusPanel({ data }: { data: ConsensusData }) {
  const { avgRisk, successfulCount, failedCount, consensusSells, consensusBuys, problemThemes } = data

  return (
    <div className="card p-5 border-blue/20 bg-blue/5">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="panel-title flex items-center gap-2">
          <Users size={14} className="text-blue-light" aria-hidden />
          Round-Table Consensus
        </h2>
        <div className="flex items-center gap-3 text-xs text-text-muted">
          <span className="flex items-center gap-1">
            <CheckCircle2 size={11} className="text-emerald" aria-hidden />
            {successfulCount} responded
          </span>
          {failedCount > 0 && (
            <span className="flex items-center gap-1">
              <XCircle size={11} className="text-rose-light" aria-hidden />
              {failedCount} failed
            </span>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Average risk */}
        <div>
          <p className="panel-title mb-1">Average Risk</p>
          {avgRisk !== null ? (
            <>
              <p className="stat-value">
                {avgRisk.toFixed(1)}<span className="text-sm text-text-muted">/10</span>
              </p>
              <div className="h-1.5 bg-surface-hover rounded-full mt-2 overflow-hidden">
                <div
                  className={clsx('h-full rounded-full', riskColor(avgRisk))}
                  style={{ width: `${(avgRisk / 10) * 100}%` }}
                />
              </div>
            </>
          ) : (
            <p className="text-xs text-text-muted italic">No responses</p>
          )}
        </div>

        {/* Common problem themes */}
        <div className="lg:col-span-3">
          <p className="panel-title mb-1">
            Common Problem Themes
          </p>
          {problemThemes.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {problemThemes.map(t => (
                <span
                  key={t.phrase}
                  className="text-[11px] px-2 py-0.5 rounded-full bg-amber/10 text-amber border border-amber/20"
                >
                  {t.phrase} <span className="text-text-muted">×{t.count}</span>
                </span>
              ))}
            </div>
          ) : (
            <p className="text-xs text-text-muted italic">
              No recurring themes found across personas
            </p>
          )}
        </div>

        {/* Consensus sells */}
        <div className="lg:col-span-2">
          <p className="panel-title mb-2 flex items-center gap-1">
            <TrendingDown size={11} className="text-rose-light" aria-hidden />
            Consensus Sells <span className="text-text-muted">(flagged by 3+)</span>
          </p>
          {consensusSells.length > 0 ? (
            <ul className="space-y-1.5">
              {consensusSells.map(s => (
                <li
                  key={s.ticker}
                  className="flex items-start gap-2 text-xs p-2 rounded bg-rose/5 border border-rose/20"
                >
                  <span className="font-bold text-rose-light shrink-0">{s.ticker}</span>
                  <span className="text-text-muted shrink-0">×{s.count}</span>
                  <span className="text-text-secondary truncate" title={s.reasons.join(' | ')}>
                    {s.reasons[0]}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-text-muted italic">No ticker flagged by 3+ personas</p>
          )}
        </div>

        {/* Consensus buys */}
        <div className="lg:col-span-2">
          <p className="panel-title mb-2 flex items-center gap-1">
            <TrendingUp size={11} className="text-emerald" aria-hidden />
            Consensus Buys <span className="text-text-muted">(flagged by 2+)</span>
          </p>
          {consensusBuys.length > 0 ? (
            <ul className="space-y-1.5">
              {consensusBuys.map(s => (
                <li
                  key={s.ticker}
                  className="flex items-start gap-2 text-xs p-2 rounded bg-emerald/5 border border-emerald/20"
                >
                  <span className="font-bold text-emerald shrink-0">{s.ticker}</span>
                  <span className="text-text-muted shrink-0">×{s.count}</span>
                  <span className="text-text-secondary truncate" title={s.reasons.join(' | ')}>
                    {s.reasons[0]}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-text-muted italic">No ticker suggested by 2+ personas</p>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Main page ───────────────────────────────────────────────────────────────

export default function PersonaRoundTablePage() {
  const [data, setData] = useState<WhatWouldAllResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasFetched, setHasFetched] = useState(false)

  const fetchAll = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<WhatWouldAllResponse>('/api/portfolio/what-would')
      setData(res.data)
      setHasFetched(true)
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? err?.message ?? 'Failed to load round-table')
    } finally {
      setLoading(false)
    }
  }

  // Auto-fetch once on mount — results are cached backend-side for 5 min.
  useEffect(() => {
    fetchAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const consensus = useMemo(() => {
    if (!data?.personas?.length) return null
    return computeConsensus(data.personas)
  }, [data])

  const personas = data?.personas ?? []

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Users size={20} className="text-blue-light" aria-hidden />
            Persona Round-Table
          </h1>
          <p className="text-sm text-text-secondary">
            All 6 legendary investors critique the discretionary portfolio side-by-side.
          </p>
        </div>
        <button
          type="button"
          onClick={fetchAll}
          disabled={loading}
          className="btn-primary gap-2 text-xs"
        >
          {loading ? (
            <Loader2 size={13} className="animate-spin" aria-hidden />
          ) : (
            <RefreshCw size={13} aria-hidden />
          )}
          {hasFetched ? 'Re-Ask All 6 Personas' : 'Ask All 6 Personas'}
        </button>
      </div>

      {/* Error banner */}
      {error && (
        <div className="card p-4 border-rose/30 bg-rose/5 flex items-start gap-2">
          <AlertTriangle size={16} className="text-rose-light shrink-0 mt-0.5" aria-hidden />
          <div>
            <p className="text-sm font-semibold text-rose-light">Round-table failed</p>
            <p className="text-xs text-text-secondary mt-0.5">{error}</p>
          </div>
        </div>
      )}

      {/* Loading skeletons */}
      {loading && !data && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[0, 1, 2, 3, 4, 5].map(i => <SkeletonCard key={i} />)}
        </div>
      )}

      {/* Persona grid */}
      {!loading && personas.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
          {personas.map(p => (
            p.error
              ? <PersonaErrorCard key={p.persona_id} persona={p} />
              : <PersonaCritiqueCard key={p.persona_id} persona={p} />
          ))}
        </div>
      )}

      {/* Consensus panel */}
      {!loading && consensus && consensus.successfulCount > 0 && (
        <ConsensusPanel data={consensus} />
      )}

      {/* Empty state */}
      {!loading && !error && personas.length === 0 && (
        <div className="card p-8 text-center">
          <Users size={28} className="text-text-muted mx-auto mb-2" aria-hidden />
          <p className="text-sm text-text-secondary">
            Click "Ask All 6 Personas" to convene the round-table.
          </p>
        </div>
      )}

      {/* Footer note */}
      {!loading && personas.length > 0 && (
        <p className="text-xs text-text-muted text-center">
          Results are cached for 5 minutes. Each persona is an LLM agent running in parallel —
          failed personas don't block the rest.
        </p>
      )}
    </div>
  )
}
